/**
 * Único lugar donde se decide cómo se muestra una moneda en el dashboard.
 *
 * Formato: "." separa miles y "," separa decimales, con 2 decimales por defecto (ej. "$ 1.234,50").
 * No usa Intl a propósito: el resultado no puede depender del idioma del sistema ni de los datos ICU del runtime.
 *
 * El catálogo de monedas vive en el backend (enum Currency): al iniciar sesión se registra acá con [registerCurrencies].
 * CURRENCIES es solo el valor por defecto mientras no cargó (y para tests): sumar una moneda nueva no requiere tocarlo.
 */

export const DEFAULT_CURRENCY = 'UYU';

/**
 * Moneda que se usa cuando una llamada a formatMoney no indica una. Es UYU en todo el sistema salvo dentro de un
 * reporte, que se emite de a una moneda: la pantalla del reporte la fija al elegirla y los gráficos/tablas del
 * reporte (que formatean sin pasar moneda) la heredan. Fuera de los reportes siempre se pasa la moneda del dato.
 */
let activeCurrency = DEFAULT_CURRENCY;
export const setActiveCurrency = (code) => {
  activeCurrency = CURRENCIES[String(code || '').toUpperCase()] ? String(code).toUpperCase() : DEFAULT_CURRENCY;
};
export const getActiveCurrency = () => activeCurrency;

export const CURRENCIES = {
  UYU: { code: 'UYU', symbol: '$', decimals: 2, isoNumeric: '0858', label: 'Pesos uruguayos' },
  USD: { code: 'USD', symbol: 'US$', decimals: 2, isoNumeric: '0840', label: 'Dólares estadounidenses' },
};

/** Registra el catálogo que informa el backend (código, símbolo, decimales, ISO numérico, nombre). */
export const registerCurrencies = (catalog) => {
  (catalog || []).forEach((item) => {
    if (!item?.code) return;
    CURRENCIES[item.code] = {
      code: item.code,
      symbol: item.symbol,
      decimals: item.decimals ?? 2,
      isoNumeric: item.isoNumeric,
      label: item.label || item.code,
      bcuPublished: Boolean(item.bcuPublished),
    };
  });
};

/** Una moneda desconocida o ausente es la moneda por defecto (todo lo histórico es UYU). */
export const getCurrency = (code) => CURRENCIES[String(code || '').toUpperCase()] || CURRENCIES[DEFAULT_CURRENCY];

export const currencySymbol = (code) => getCurrency(code).symbol;

export const currencyOptions = (enabled) =>
  (enabled && enabled.length ? enabled : [DEFAULT_CURRENCY]).map(getCurrency);

/** Redondeo half-up a [decimals] decimales sin el error de coma flotante de toFixed (1.005 -> 1.01). */
const roundHalfUp = (value, decimals) => Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);

/** Número con "." de miles y "," de decimales. null/undefined/NaN -> ''. */
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  const negative = number < 0;
  const rounded = roundHalfUp(Math.abs(number), decimals);
  const [integer, fraction] = rounded.toFixed(decimals).split('.');
  const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const text = fraction ? `${withThousands},${fraction}` : withThousands;
  // "-0,00" no se muestra: un valor que redondea a cero no es negativo.
  return negative && rounded !== 0 ? `-${text}` : text;
}

/**
 * Monto con su símbolo: formatMoney(1234.5) -> "$ 1.234,50"; formatMoney(10, 'USD') -> "US$ 10,00".
 * @param {number|string|null} value
 * @param {string} [currency] código de moneda (por defecto UYU)
 * @param {{decimals?: number, symbol?: boolean, fallback?: string}} [options]
 *   decimals: cantidad de decimales (por defecto los de la moneda); symbol: false para omitir el símbolo;
 *   fallback: qué mostrar si el valor no es un número ('-' por defecto).
 */
export function formatMoney(value, currency = activeCurrency, options = {}) {
  const { decimals, symbol = true, fallback = '-' } = options;
  const config = getCurrency(currency);
  const text = formatNumber(value, decimals ?? config.decimals);
  if (text === '') return fallback;
  if (!symbol) return text;
  return text.startsWith('-') ? `-${config.symbol} ${text.slice(1)}` : `${config.symbol} ${text}`;
}

/** Interpreta lo que escribe una persona ("1.234,50", "1234,5", "1234.50") como número; null si no es válido. */
export function parseMoney(input) {
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;
  if (input === null || input === undefined) return null;
  let text = String(input).replace(/[^\d.,-]/g, '');
  if (text === '' || text === '-') return null;
  if (text.includes(',')) {
    text = text.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) {
    text = text.replace(/\./g, '');
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
}

/**
 * Convierte entre monedas pasando por UYU. `rates` = { USD: 40.286 } (UYU por 1 unidad). null si falta una cotización.
 */
export function convertMoney(amount, from, to, rates = {}) {
  const source = getCurrency(from).code;
  const target = getCurrency(to).code;
  if (source === target) return amount;
  const rateOf = (code) => (code === DEFAULT_CURRENCY ? 1 : Number(rates[code]));
  const fromRate = rateOf(source);
  const toRate = rateOf(target);
  if (!(fromRate > 0) || !(toRate > 0)) return null;
  return (amount * fromRate) / toRate;
}

/**
 * Leyenda para mostrar una conversión, ej. "US$ 10,00 · cotización $ 40,286 (30/09/2026) · $ 402,86".
 * quote: { amount, currency, rate, rateDate, baseAmount }.
 */
export function formatConversion(quote) {
  if (!quote) return '';
  const date = quote.rateDate ? String(quote.rateDate).split('-').reverse().join('/') : '';
  const rate = `cotización $ ${formatNumber(quote.rate, 3)}${date ? ` (${date})` : ''}`;
  return `${formatMoney(quote.amount, quote.currency)} · ${rate} · ${formatMoney(quote.baseAmount, DEFAULT_CURRENCY)}`;
}
